import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { Alert } from '../components/Alert';
import { getEnv } from '../config';
import axios from 'axios';

const API_URL = getEnv('REACT_APP_API_URL') || '';

const PHOTOS = [
    'DSC08867.JPG', 'DSC07053.JPG', 'DSC09159.JPG', 'DSC06358.JPG',
    'DSC09978.JPG', 'DSC08367.JPG', 'DSC06717.JPG', 'DSC08195.JPG',
    'DSC09583.JPG', 'DSC06771.JPG', 'DSC08747.JPG', 'DSC09139.JPG',
];

const ALL_PHOTOS = [
    'DSC05699.JPG','DSC05996.JPG','DSC06003.JPG','DSC06358.JPG','DSC06717.JPG',
    'DSC06735.JPG','DSC06771.JPG','DSC06936.JPG','DSC06938.JPG','DSC06944.JPG',
    'DSC06987.JPG','DSC07014.JPG','DSC07053.JPG','DSC07059.JPG','DSC08176.JPG',
    'DSC08195.JPG','DSC08204.JPG','DSC08232.JPG','DSC08367.JPG','DSC08455.JPG',
    'DSC08491.JPG','DSC08593.JPG','DSC08710.JPG','DSC08737.JPG','DSC08747.JPG',
    'DSC08763.JPG','DSC08785.JPG','DSC08793.JPG','DSC08854.JPG','DSC08867.JPG',
    'DSC09119.JPG','DSC09139.JPG','DSC09159.JPG','DSC09577.JPG','DSC09583.JPG',
    'DSC09604.JPG','DSC09627.JPG','DSC09631.JPG','DSC09687.JPG','DSC09978.JPG',
];

function PhotoCell({ index }) {
    const baseOpacity = useRef(0.3 + Math.random() * 0.4);
    const [layers, setLayers] = useState(() => [
        { src: PHOTOS[index % PHOTOS.length], opacity: baseOpacity.current, key: 0 },
    ]);
    const keyRef = useRef(1);

    useEffect(() => {
        const delay = 4000 + Math.random() * 16000;
        const interval = 10000 + Math.random() * 20000;

        const timeout = setTimeout(() => {
            const swap = () => {
                const newPhoto = ALL_PHOTOS[Math.floor(Math.random() * ALL_PHOTOS.length)];
                const newOpacity = 0.3 + Math.random() * 0.4;
                const newKey = keyRef.current++;
                setLayers((prev) => {
                    const top = prev[prev.length - 1];
                    return [top, { src: newPhoto, opacity: newOpacity, key: newKey }];
                });
            };
            swap();
            const id = setInterval(swap, interval);
            return () => clearInterval(id);
        }, delay);

        return () => clearTimeout(timeout);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="relative overflow-hidden">
            {layers.map((layer, i) => (
                <img
                    key={layer.key}
                    src={`/photos/${layer.src}`}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                        opacity: i === layers.length - 1 ? layer.opacity : 0,
                        transition: 'opacity 2s ease-in-out',
                    }}
                    loading="lazy"
                />
            ))}
        </div>
    );
}

function PhotoGrid() {
    return (
        <div className="absolute inset-0 grid grid-cols-3 sm:grid-cols-4 grid-rows-4 sm:grid-rows-3 gap-0 overflow-hidden">
            {PHOTOS.map((_, i) => (
                <PhotoCell key={i} index={i} />
            ))}
        </div>
    );
}

export function Login() {
    const { loginWithOAuth, loading, error, setError, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const appleScriptLoaded = useRef(false);

    const googleClientId = getEnv('REACT_APP_GOOGLE_CLIENT_ID');
    const appleClientId = getEnv('REACT_APP_APPLE_CLIENT_ID');

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/dashboard');
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (!appleClientId || appleScriptLoaded.current) return;
        const script = document.createElement('script');
        script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
        script.async = true;
        script.onload = () => { appleScriptLoaded.current = true; };
        document.head.appendChild(script);
        return () => { document.head.removeChild(script); };
    }, [appleClientId]);

    const handleGoogleSuccess = async (response) => {
        const success = await loginWithOAuth(response.credential, 'google');
        if (success) navigate('/dashboard');
    };

    const handleGoogleError = () => {
        setError('Google sign-in failed. Please try again.');
    };

    const logToBackend = (msg) => {
        try { axios.post(`${API_URL}/logs`, { level: 'error', message: msg }); } catch {}
    };

    const handleAppleSignIn = async () => {
        try {
            if (!window.AppleID) {
                const msg = `Apple SDK not loaded. appleClientId=${appleClientId}, scripts=${document.querySelectorAll('script[src*="appleid"]').length}`;
                logToBackend(msg);
                setError(msg);
                return;
            }
            const redirectURI = window.location.origin + '/login';
            logToBackend(`Apple signIn starting: clientId=${appleClientId}, redirectURI=${redirectURI}`);
            window.AppleID.auth.init({ clientId: appleClientId, scope: 'email name', redirectURI, usePopup: true });
            const response = await window.AppleID.auth.signIn();
            logToBackend(`Apple signIn response received: hasCode=${!!response?.authorization?.code}, hasIdToken=${!!response?.authorization?.id_token}`);
            const code = response.authorization.code;
            const idToken = response.authorization.id_token;
            const success = await loginWithOAuth(idToken, 'apple', { code, redirectURI });
            if (success) navigate('/dashboard');
        } catch (err) {
            const errDetail = JSON.stringify({ error: err.error, message: err.message, type: err.type, stack: err.stack?.substring(0, 500) });
            logToBackend(`Apple signIn FAILED: ${errDetail}`);
            console.error('Apple Sign In error:', err);
            if (err.error !== 'popup_closed_by_user') {
                setError(`Apple sign-in failed: ${err.error || err.message || 'Unknown error'}. Check console for details.`);
            }
        }
    };

    return (
        <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden bg-black">
            <PhotoGrid />
            <div className="absolute inset-0 bg-black/50" />
            <div className="relative z-10 w-full max-w-sm bg-white/95 dark:bg-slate-800/95 backdrop-blur rounded-xl shadow-2xl p-8 animate-fade-in">
                <div className="text-center mb-8">
                    <img src="/rt-logo.png" alt="RT Care Groups" className="w-16 h-16 mx-auto mb-4 rounded-lg bg-white dark:bg-slate-100 p-1.5" />
                    <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">RT Care Groups</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Leader Portal</p>
                </div>

                {error && (
                    <Alert type="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <div className="space-y-3">
                    {googleClientId && (
                        <div className="flex justify-center">
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={handleGoogleError}
                                theme="outline"
                                size="large"
                                width="300"
                                text="signin_with"
                            />
                        </div>
                    )}

                    {appleClientId && (
                        <button
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-black text-white font-medium text-sm hover:bg-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500/30"
                            onClick={handleAppleSignIn}
                            disabled={loading}
                        >
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                            </svg>
                            Sign in with Apple
                        </button>
                    )}

                    {!googleClientId && !appleClientId && (
                        <div className="text-center text-sm text-slate-500 dark:text-slate-400 space-y-1">
                            <p>OAuth sign-in is not configured.</p>
                            <p>Set <code className="text-xs bg-slate-100 dark:bg-slate-700 px-1 py-0.5 rounded">REACT_APP_GOOGLE_CLIENT_ID</code> or <code className="text-xs bg-slate-100 dark:bg-slate-700 px-1 py-0.5 rounded">REACT_APP_APPLE_CLIENT_ID</code> to enable sign-in.</p>
                        </div>
                    )}
                </div>

                {loading && <p className="text-center text-sm text-accent-500 mt-4">Signing in...</p>}
            </div>
        </div>
    );
}
